import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  FileText,
  Search,
  ChevronDown,
  Calendar,
  X,
  Filter,
  MoreVertical,
  Eye,
  Mail,
  Inbox,
  Trash2,
  Loader2,
  Circle,
  CheckCircle,
} from 'lucide-react'
import type { ReportsListProps, Report, SourceType, ReportFilterParams } from './types'
import { Pagination } from '../ui/Pagination'

interface FilterState {
  streamId: string | null
  sourceType: SourceType | null
  dateFrom: string | null
  dateTo: string | null
  search: string
}

// Helper to parse URL params into filter state
function getFiltersFromUrl(): FilterState {
  if (typeof window === 'undefined') {
    return {
      streamId: null,
      sourceType: null,
      dateFrom: null,
      dateTo: null,
      search: '',
    }
  }

  const params = new URLSearchParams(window.location.search)
  return {
    streamId: params.get('stream') || null,
    sourceType: (params.get('source') as SourceType) || null,
    dateFrom: params.get('from') || null,
    dateTo: params.get('to') || null,
    search: params.get('q') || '',
  }
}

// Convert FilterState to ReportFilterParams for callbacks
function toFilterParams(filters: FilterState): ReportFilterParams {
  return {
    streamId: filters.streamId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: filters.search,
  }
}

interface ExtendedReportsListProps extends ReportsListProps {
  onMarkSeen?: (id: string) => void
  onMarkUnseen?: (id: string) => void
}

export function ReportsList({
  reports,
  streams = [],
  pagination,
  onView,
  onSendEmail,
  onViewItems,
  onDelete,
  onPageChange,
  onFilterChange,
  onMarkSeen,
  onMarkUnseen,
}: ExtendedReportsListProps) {
  const [filters, setFilters] = useState<FilterState>(getFiltersFromUrl)

  // Show filters if any URL params are set
  const [showFilters, setShowFilters] = useState(() => {
    const initialFilters = getFiltersFromUrl()
    return !!(
      initialFilters.streamId ||
      initialFilters.sourceType ||
      initialFilters.dateFrom ||
      initialFilters.dateTo
    )
  })

  // Debounce timer for search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Apply filter changes to server (debounced for search)
  const applyFilters = useCallback((newFilters: FilterState, immediate = false) => {
    if (onFilterChange) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      if (immediate || !newFilters.search) {
        onFilterChange(toFilterParams(newFilters))
      } else {
        // Debounce search to avoid too many requests
        searchTimeoutRef.current = setTimeout(() => {
          onFilterChange(toFilterParams(newFilters))
        }, 300)
      }
    }
  }, [onFilterChange])

  // Update filters and trigger server request
  const updateFilters = useCallback((updater: (prev: FilterState) => FilterState, immediate = false) => {
    setFilters(prev => {
      const newFilters = updater(prev)
      applyFilters(newFilters, immediate)
      return newFilters
    })
  }, [applyFilters])

  // Source types are no longer available in list view since items aren't included
  // Keep an empty array to not break the filter UI
  const sourceTypes: SourceType[] = []

  // Reports are already filtered by server, just use them directly
  const filteredReports = reports

  const activeFilterCount = [
    filters.streamId,
    filters.sourceType,
    filters.dateFrom || filters.dateTo,
  ].filter(Boolean).length

  const clearFilters = () => {
    updateFilters(() => ({
      streamId: null,
      sourceType: null,
      dateFrom: null,
      dateTo: null,
      search: '',
    }), true)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatSourceType = (type: SourceType) => {
    const labels: Record<SourceType, string> = {
      email: 'Email',
      webhook: 'Webhook',
      csv: 'CSV',
    }
    return labels[type] || type
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                Reports
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                {filteredReports.length} of {pagination?.totalCount ?? reports.length} report{(pagination?.totalCount ?? reports.length) !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Search and Filter Toggle */}
          <div className="flex items-center gap-3 mt-5">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500" />
              <input
                type="text"
                placeholder="Search reports..."
                value={filters.search}
                onChange={(e) =>
                  updateFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="w-full pl-9 pr-4 py-2 text-sm bg-stone-100 dark:bg-stone-800 border border-transparent focus:border-teal-500 dark:focus:border-teal-400 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 outline-none transition-colors"
              />
              {filters.search && (
                <button
                  onClick={() => updateFilters((prev) => ({ ...prev, search: '' }), true)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors flex-shrink-0 ${
                showFilters || activeFilterCount > 0
                  ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-600 text-white text-xs font-medium">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-800">
              <div className="flex flex-wrap items-end gap-4">
                {/* Stream Filter */}
                {streams.length > 0 && (
                  <div className="flex-1 min-w-[180px] max-w-[220px]">
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                      Stream
                    </label>
                    <div className="relative">
                      <select
                        value={filters.streamId || ''}
                        onChange={(e) =>
                          updateFilters((prev) => ({
                            ...prev,
                            streamId: e.target.value || null,
                          }), true)
                        }
                        className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                      >
                        <option value="">All streams</option>
                        {streams.map((stream) => (
                          <option key={stream.id} value={stream.id}>
                            {stream.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Source Type Filter */}
                <div className="flex-1 min-w-[140px] max-w-[180px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Source
                  </label>
                  <div className="relative">
                    <select
                      value={filters.sourceType || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          sourceType: (e.target.value as SourceType) || null,
                        }), true)
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="">All sources</option>
                      {sourceTypes.map((type) => (
                        <option key={type} value={type}>
                          {formatSourceType(type)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Date From */}
                <div className="flex-1 min-w-[140px] max-w-[160px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    From
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.dateFrom || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          dateFrom: e.target.value || null,
                        }), true)
                      }
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Date To */}
                <div className="flex-1 min-w-[140px] max-w-[160px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    To
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.dateTo || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          dateTo: e.target.value || null,
                        }), true)
                      }
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Reports List */}
      <main className="max-w-5xl mx-auto px-6 py-6">
        {filteredReports.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 dark:bg-stone-800 mb-4">
              <FileText className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <p className="text-stone-600 dark:text-stone-400 font-medium">
              No reports found
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-500 mt-1">
              {activeFilterCount > 0
                ? 'Try adjusting your filters'
                : 'Reports will appear here once generated'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {filteredReports.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  onView={onView ? () => onView(report.id) : undefined}
                  onSendEmail={onSendEmail ? () => onSendEmail(report.id) : undefined}
                  onViewItems={onViewItems ? () => onViewItems(report.id) : undefined}
                  onDelete={onDelete ? () => onDelete(report.id) : undefined}
                  onMarkSeen={onMarkSeen ? () => onMarkSeen(report.id) : undefined}
                  onMarkUnseen={onMarkUnseen ? () => onMarkUnseen(report.id) : undefined}
                />
              ))}
            </div>

            {pagination && onPageChange && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                perPage={pagination.perPage}
                onPageChange={(page) => onPageChange(page, toFilterParams(filters))}
              />
            )}
          </>
        )}
      </main>
    </div>
  )
}

interface ReportRowProps {
  report: Report
  onView?: () => void
  onSendEmail?: () => void
  onViewItems?: () => void
  onDelete?: () => void
  onMarkSeen?: () => void
  onMarkUnseen?: () => void
}

function ReportRow({ report, onView, onSendEmail, onViewItems, onDelete, onMarkSeen, onMarkUnseen }: ReportRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const formattedDate = new Date(report.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  const handleAction = (action?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    action?.()
  }

  // Truncate overview to reasonable length
  const truncatedOverview =
    report.overview.length > 140
      ? report.overview.slice(0, 140) + '...'
      : report.overview

  const isProcessing = report.status === 'pending'
  const itemCount = report.itemCount ?? 0
  const insightCount = report.insightCount ?? report.questionAnswers?.length ?? 0
  const gemCount = report.gemCount ?? report.gems?.length ?? 0

  return (
    <div
      onClick={onView}
      className="w-full text-left group rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-5 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-start gap-4">
        {/* Icon with unseen indicator */}
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/40 transition-colors">
            {isProcessing ? (
              <Loader2 className="w-5 h-5 text-teal-500 dark:text-teal-400 animate-spin" />
            ) : (
              <FileText className="w-5 h-5 text-stone-500 dark:text-stone-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
            )}
          </div>
          {!report.seen && !isProcessing && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-teal-500 border-2 border-white dark:border-stone-900" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-medium text-stone-900 dark:text-stone-100 truncate group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              {report.reportNumber && (
                <span className="text-stone-400 dark:text-stone-500 font-normal mr-1">
                  #{report.reportNumber}
                </span>
              )}
              {report.title}
            </h3>
            {isProcessing && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 text-xs font-medium">
                Processing...
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            {/* Stream badge */}
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 text-xs font-medium">
              <span className="w-1 h-1 rounded-full bg-teal-500 dark:bg-teal-400" />
              {report.streamName}
            </span>

            {/* Date */}
            <span className="flex items-center gap-1 text-xs text-stone-400 dark:text-stone-500">
              <Calendar className="w-3 h-3" />
              {formattedDate}
            </span>

            {/* Mobile stats */}
            <span className="sm:hidden text-xs text-stone-400 dark:text-stone-500">
              · {itemCount} items
            </span>
          </div>

          {/* Overview excerpt or processing message */}
          {isProcessing ? (
            <p className="text-sm text-teal-600 dark:text-teal-400 leading-relaxed">
              Analyzing {itemCount} items... This may take a minute.
            </p>
          ) : (
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed line-clamp-2">
              {truncatedOverview}
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="flex-shrink-0 hidden sm:flex flex-col items-end gap-1 text-xs text-stone-400 dark:text-stone-500">
          <span>{insightCount} insight{insightCount !== 1 ? 's' : ''}</span>
          <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          {gemCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              {gemCount} gem{gemCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Action Menu */}
        <div ref={menuRef} className="relative flex-shrink-0">
          <button
            onClick={handleMenuClick}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 py-1 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 z-10">
              <button
                onClick={handleAction(onView)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Eye className="w-4 h-4" />
                View Report
              </button>

              <button
                onClick={handleAction(onSendEmail)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Mail className="w-4 h-4" />
                Email Report
              </button>

              <button
                onClick={handleAction(onViewItems)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Inbox className="w-4 h-4" />
                View Items
              </button>

              {report.seen ? (
                <button
                  onClick={handleAction(onMarkUnseen)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                >
                  <Circle className="w-4 h-4" />
                  Mark as Unread
                </button>
              ) : (
                <button
                  onClick={handleAction(onMarkSeen)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Read
                </button>
              )}

              <div className="border-t border-stone-200 dark:border-stone-700 my-1" />

              <button
                onClick={handleAction(onDelete)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
